import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  ColumnHint,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  initials,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';


import { getOrg, viewAsOrg } from '../../api/admin.js';
import { ApiError } from '../../lib/api-client.js';

export function AdminOrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const orgQuery = useQuery({
    queryKey: ['admin-org', id],
    queryFn: () => getOrg(id!),
    enabled: Boolean(id),
  });

  const viewAsMutation = useMutation({
    mutationFn: () => viewAsOrg(id!),
    onSuccess: (data) => {
      const isLocalDev = window.location.hostname.endsWith('localhost');
      if (isLocalDev) {
        window.localStorage.setItem('sitewise:subdomain-override', data.orgSlug);
        window.location.assign(`/handoff#handoff=${encodeURIComponent(data.handoffToken)}`);
      } else {
        const base = window.location.host.split('.').slice(-2).join('.');
        window.location.assign(
          `${window.location.protocol}//${data.orgSlug}.${base}/handoff#handoff=${encodeURIComponent(data.handoffToken)}`,
        );
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not start view-as session');
    },
  });

  if (orgQuery.isPending) return <LoadingState rows={4} />;
  if (orgQuery.isError || !orgQuery.data) {
    return (
      <Alert variant="danger">
        <AlertDescription>Organization not found.</AlertDescription>
      </Alert>
    );
  }

  const { org, users } = orgQuery.data;

  return (
    <>
      <Link
        to="/admin/orgs"
        className="mb-3 inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to organizations
      </Link>

      <PageHeader
        title={org.name}
        description={`Slug: ${org.slug} · ${users.length} active user${users.length === 1 ? '' : 's'}`}
        action={
          <Button onClick={() => viewAsMutation.mutate()} disabled={viewAsMutation.isPending}>
            <ExternalLink className="h-4 w-4" />
            {viewAsMutation.isPending ? 'Opening…' : 'View as this org'}
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase font-semibold text-fg-muted">Created</p>
            <p className="mt-1 text-sm font-medium">
              {new Date(org.createdAt).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase font-semibold text-fg-muted">Brand color</p>
            <p className="mt-1 inline-flex items-center gap-2 text-sm">
              <span
                className="inline-block h-4 w-4 rounded border border-border"
                style={{ backgroundColor: org.primaryColorHex ?? 'var(--color-primary)' }}
              />
              <code className="font-mono text-xs">
                {org.primaryColorHex ?? 'platform default'}
              </code>
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase font-semibold text-fg-muted">Logo</p>
            <p className="mt-1 text-sm">
              {org.logoUrl ? (
                <a
                  href={org.logoUrl}
                  className="text-admin-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
              ) : (
                <span className="text-fg-muted">Not set</span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      <h2 className="mt-8 mb-3 text-base font-semibold">Members</h2>
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <ColumnHint description="The person's name.">Name</ColumnHint>
              </TableHead>
              <TableHead>
                <ColumnHint description="The address they use to sign in.">Email</ColumnHint>
              </TableHead>
              <TableHead>
                <ColumnHint description="Their role inside this organization.">Role</ColumnHint>
              </TableHead>
              <TableHead>
                <ColumnHint description="Whether they have two-factor authentication turned on.">2FA</ColumnHint>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback>{initials(user.name || user.email)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{user.name}</span>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant="default">{user.role}</Badge>
                </TableCell>
                <TableCell>
                  {user.totpEnabled ? (
                    <Badge variant="success">Enabled</Badge>
                  ) : (
                    <Badge variant="default">Off</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
